import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Antibiotikaprofylax = {
    id: number;
    code: string;
    displayName: string;
    ordning: number;
};

export type AntibiotikaprofylaxDisplay = {
    value: Antibiotikaprofylax["id"];
    label: string;
};

type State = BaseState & {
    antibiotikaprofylax: Map<Antibiotikaprofylax["id"], Antibiotikaprofylax>;
};

export const convertAntibiotikaprofylaxData: (args: {
    antibiotikaprofylax?: Antibiotikaprofylax;
}) => AntibiotikaprofylaxDisplay | null = ({ antibiotikaprofylax }) => {
    if (!antibiotikaprofylax) {
        return null;
    }

    return {
        value: antibiotikaprofylax.id,
        label: antibiotikaprofylax.displayName,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        antibiotikaprofylax: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "antibiotikaprofylax";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        antibiotikaprofylax: (state: State) => [...state.antibiotikaprofylax.values()],
        antibiotikaprofylaxSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) =>
                    convertAntibiotikaprofylaxData({ antibiotikaprofylax: singleResult })
                );
            }

            return result;
        },
        antibiotikaprofylaxById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.antibiotikaprofylax.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addAntibiotikaprofylax: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                antibiotikaprofylax = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                antibiotikaprofylax: Antibiotikaprofylax[];
            }
        ) =>
            subsetSetter<State, Antibiotikaprofylax>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: antibiotikaprofylax,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchAntibiotikaprofylax: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Antibiotikaprofylax>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/antibiotikaprofylaxer/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addAntibiotikaprofylax", {
                    subsetName,
                    offset: resultOffset,
                    antibiotikaprofylax: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) =>
                              convertAntibiotikaprofylaxData({ antibiotikaprofylax: singleResult })
                          )
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
